import { useMemo } from "react";
import { makeStyles } from "tss-react/mui";
import { MiIcon } from "@miplatform/components";
import { getFullName, getInitials } from "@miplatform/utils";
import { mdiClose } from "@mdi/js";
import {
  Autocomplete,
  MenuItem,
  TextField,
  Typography,
  createFilterOptions,
} from "@mui/material";
import { Box } from "@mui/system";

export const UserAutocomplete = ({
  label,
  options,
  onChange,
  value,
  multiple,
  freeSolo,
  clearOnBlur,
  error,
  helperText,
  limitTags,
  valueKey = "id",
  required,
  ...props
}) => {
  const { theme, classes } = useStyles();
  const selectedUser = useMemo(
    () => options.find((user) => user[valueKey] === value),
    [options, value, valueKey]
  );

  const autocompleteFilterOptions = createFilterOptions({
    ignoreCase: true,
    matchFrom: "start",
    limit: 40,
  });

  const getOptionName = (option) =>
    option.name ||
    (option.firstName && option.lastName
      ? getFullName(option.firstName, option.lastName)
      : "") ||
    option.displayName ||
    "";

  const renderUserOption = (option, getItemProps, index) => (
    <Box display="flex" alignItems="center">
      <Box className={classes.avatar} width={20} height={20} mr={1}>
        <Typography fontSize={10} color="common.white">
          {getInitials(getOptionName(option))}
        </Typography>
      </Box>
      <Typography variant="body2" flexGrow={1}>
        {option.name ||
          option.displayName ||
          (option.firstName && option.lastName
            ? getFullName(option.firstName, option.lastName)
            : "")}
        {option.title ? ` | ${option.title}` : ""}
      </Typography>
      {getItemProps && typeof index !== "undefined" && (
        <Box
          sx={{
            ml: 2,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            borderRadius: "50%",
            bgcolor: "grey.300",
            width: 17,
            height: 17,
          }}
        >
          <MiIcon
            path={mdiClose}
            size={0.5}
            color={theme.palette.grey.dark}
            onClick={getItemProps({ index }).onDelete}
          />
        </Box>
      )}
    </Box>
  );

  return (
    <Autocomplete
      disableClearable
      multiple={multiple}
      freeSolo={freeSolo}
      clearOnBlur={clearOnBlur}
      limitTags={limitTags}
      data-testId={props["data-testId"] || "mi-user-autocomplete"}
      options={options}
      value={multiple ? value : selectedUser || null}
      getOptionLabel={(option) => {
        if (typeof option === "string") {
          return option;
        }

        if (option && option[valueKey]) {
          return getOptionName(option);
        }

        return "";
      }}
      filterOptions={autocompleteFilterOptions}
      getOptionKey={(option) => option[valueKey]}
      isOptionEqualToValue={(option, val) =>
        option[valueKey] === val?.[valueKey]
      }
      onChange={(_e, newValue) => {
        if (multiple) {
          onChange(newValue);
        } else {
          onChange(newValue[valueKey]);
        }
      }}
      renderInput={(params) => (
        <TextField
          {...params}
          required={required}
          size="small"
          label={label}
          placeholder={multiple ? label : ""}
          error={!!error}
          helperText={error ? error.message : helperText}
          onKeyDown={(e) => {
            if (
              e.key === "Backspace" &&
              !params.inputProps.value &&
              !multiple
            ) {
              onChange("");
            }
          }}
        />
      )}
      renderValue={(selected, getItemProps) =>
        Array.isArray(selected)
          ? selected.map((option, index) => (
              <MenuItem key={option.id} sx={{ height: 20, p: 2 }}>
                {renderUserOption(option, getItemProps, index)}
              </MenuItem>
            ))
          : selectedUser && (
              <MenuItem sx={{ height: 20, p: 2 }}>
                {renderUserOption(selectedUser)}
              </MenuItem>
            )
      }
      renderOption={(props, option) => (
        <MenuItem {...props} key={option.id || option.email}>
          {renderUserOption(option)}
        </MenuItem>
      )}
    />
  );
};

const useStyles = makeStyles()((theme) => ({
  chip: {
    borderRadius: theme.spacing(1),
    backgroundColor: theme.palette.grey.mediumLight,
    margin: theme.spacing(0, 0.5, 0.5, 0),
    height: 34,
  },
  avatar: {
    borderRadius: "50%",
    backgroundColor: theme.palette.grey.medium,
    color: theme.palette.common.white,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
}));
